/**
 * Overview Tab
 * 
 * Asset scope: Shows aggregate metrics + positions table
 * Position scope: Shows account metadata
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/portfolioCalculations';
import { CurrencyAmount } from '@/components/ui/CurrencyAmount';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import type { InstrumentScope } from '@/hooks/useInstrumentDetail';
import { getCurrencyLabel } from '@/lib/currency';

interface OverviewTabProps {
  asset: UnifiedAsset;
  data: any;
  scope: InstrumentScope;
  onPositionClick: (accountId: string) => void;
}

export function OverviewTab({ asset, data, scope, onPositionClick }: OverviewTabProps) {

  // Asset scope: show only overview fields; no aggregates or duplicate holdings table
  const positions: any[] = Array.isArray(data?.positions) ? data.positions : [];
  const isManual = (asset as any)?.source_type === 'manual';
  const isAccount = (asset as any)?.source_type === 'account';
  const isPositionScope = scope === 'position';
  const showAggregates = false;
  const totalValue = 0;
  const totalQuantity = 0;
  const pricePerUnit = asset.quantity && asset.quantity > 0 ? asset.market_value / asset.quantity : null;
  const aggregatedBookValue = positions.reduce((sum, p) => sum + (typeof p?.book_value === 'number' ? p.book_value : 0), 0);
  const hasAnyPositionBook = positions.some((p: any) => typeof p?.book_value === 'number');
  const manualBook = (asset as any)?.book_value as number | null | undefined;
  const bookValue = (asset as any)?.source_type === 'manual'
    ? manualBook ?? null
    : (hasAnyPositionBook ? aggregatedBookValue : null);
  const idLabel = 'Symbol';
  const holdingsTotal = positions.reduce((sum, p) => sum + (p.market_value || 0), 0);
  const holdingsColorClass = holdingsTotal > 0 ? 'text-green-600' : holdingsTotal < 0 ? 'text-destructive' : '';
  const symbol = (asset as any)?.symbol || '—';
  const institutionMeta = data?.accountMetadata;

  // Determine a single, DRY meta timestamp labeled "Updated As-of"
  // Priority by scope: position -> asset.as_of | positions max(as_of) | accountMetadata.last_synced
  // asset (account) -> accountMetadata.last_synced | positions max(as_of) | asset.updated_at | asset.as_of
  // manual -> asset.as_of | asset.updated_at
  const positionsMaxAsOf = (() => {
    const times = positions
      .map((p: any) => p?.as_of)
      .filter(Boolean)
      .map((d: string) => new Date(d).getTime());
    return times.length ? new Date(Math.max(...times)).toISOString() : null;
  })();
  const updatedAsOf: string | null = (() => {
    if (isPositionScope) return (asset as any)?.as_of || positionsMaxAsOf || (institutionMeta?.last_synced ?? null);
    if (isAccount) return (institutionMeta?.last_synced ?? null) || positionsMaxAsOf || (asset as any)?.updated_at || (asset as any)?.as_of || null;
    // manual
    return (asset as any)?.as_of || (asset as any)?.updated_at || null;
  })();
  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return '—';
    }
  };
  const formatDateOnly = (iso?: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  return (
    <div className="space-y-6">
      {/* Details (always visible) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-sm text-muted-foreground">{idLabel}</p>
              <p className="font-semibold">{symbol}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Currency</p>
              <p className="font-mono tabular-nums font-semibold">{getCurrencyLabel(asset.currency)}</p>
            </div>
            {scope === 'asset' && isAccount ? (
              <div>
                <p className="text-sm text-muted-foreground">Holdings Value</p>
                <p className={`font-semibold ${holdingsColorClass}`}>
                  <CurrencyAmount value={holdingsTotal} currency={asset.currency} showCurrency={false} className="text-sm" />
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground">Price per Unit</p>
                <p className="font-semibold">
                  {pricePerUnit != null
                    ? <CurrencyAmount value={pricePerUnit} currency={asset.currency} showCurrency={false} showDecimals={true} className="text-sm" />
                    : '—'}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">Book Value</p>
              <p className="font-semibold">
                {bookValue != null
                  ? <CurrencyAmount value={bookValue} currency={asset.currency} showCurrency={true} showDecimals={true} className="text-sm" />
                  : '—'}
              </p>
            </div>
            {(!isManual && institutionMeta) && (
              <div className="sm:col-span-2 md:col-span-3 min-w-0">
                <p className="text-sm text-muted-foreground">Institution</p>
                <p className="font-medium flex items-center gap-1.5 truncate" title={institutionMeta.institution_name || undefined}>
                  {institutionMeta.institution_logo_url ? (
                    <img
                      src={institutionMeta.institution_logo_url}
                      alt={institutionMeta.institution_name || 'Institution'}
                      className="h-3.5 w-3.5 rounded-sm object-contain flex-none"
                      loading="lazy"
                    />
                  ) : (
                    <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm bg-muted text-[9px] text-muted-foreground flex-none">
                      {(institutionMeta.institution_name || '?').charAt(0)}
                    </span>
                  )}
                  <span className="truncate">{institutionMeta.institution_name}</span>
                </p>
              </div>
            )}
            <div className="sm:col-span-2 md:col-span-3">
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="font-medium whitespace-pre-wrap">{asset.description || '—'}</p>
            </div>
          </div>

          {/* Description moved to its own card below */}
        </CardContent>
      </Card>


      {/* Aggregates and duplicate holdings table removed per design */}

      {/* Provider info row removed to avoid duplication with Meta */}

      {/* Meta (small) */}
      <div className="text-xs text-muted-foreground">
        {isManual && (
          <span>Created: {formatDateOnly((asset as any)?.created_at)}</span>
        )}
        <span className={isManual ? 'ml-4' : undefined}>Updated As-of: {formatDateTime(updatedAsOf)}</span>
      </div>
    </div>
  );
}
